import React, { useEffect, useState } from "react";
import API from "../api";
import { useAuth } from "../context/AuthContext";
import { Alert, Button } from "react-bootstrap";

function formatDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function SigninSheetPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user?.leagueid) return;
    API.get(`/league/${user.leagueid}/signin-sheet`)
      .then((res) => setData(res.data))
      .catch((err) => setError(err?.response?.data?.error || "Failed to load sign-in sheet."));
  }, [user?.leagueid]);

  if (!user?.leagueid) return <Alert variant="warning">You are not assigned to a league.</Alert>;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!data) return <div className="p-4">Loading…</div>;

  const heading = `${data.league_day || ""} ${data.league_name || ""} League`.trim();
  const weeks = data.weeks?.length ? data.weeks : [{ week_number: null, date: null }];

  return (
    <div id="print-schedule" className="signin-sheet-wrapper">
      <style>{`
        .signin-sheet-wrapper {
          padding: 2rem 1rem;
        }
        .signin-sheet {
          width: 100%;
          max-width: 800px;
          margin: 0 auto 2rem auto;
        }
        .signin-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 1rem;
          gap: 0.25rem;
        }
        .signin-title { font-size: 1.6rem; font-weight: 600; margin: 0; text-align: center; }
        .signin-date { font-size: 1.1rem; }
        .signin-table {
          width: 6.5in;
          max-width: 100%;
          margin: 0 auto;
          border-collapse: collapse;
          font-size: 0.95rem;
        }
        .signin-table th, .signin-table td {
          border: 1px solid #333;
          padding: 0.5rem 0.75rem;
          vertical-align: middle;
        }
        .signin-table thead th { background: #f1f1f1; text-align: left; }
        .signin-table .col-team, .signin-table .col-cart {
          text-align: center;
          width: 70px;
        }
        .signin-table .col-name { width: 35%; }
        .signin-table .col-sig { width: auto; }
        .print-actions { margin-top: 1rem; display: flex; gap: 0.5rem; justify-content: center; }
        @media print {
          @page { size: portrait; margin: 0; }
          .signin-sheet-wrapper { min-height: auto; padding: 0; display: block; }
          .signin-sheet {
            max-width: none;
            width: 100%;
            margin: 0;
            padding: 0.3in 0.5in;
            page-break-after: always;
          }
          .signin-sheet:last-child { page-break-after: auto; }
          .signin-title { font-size: 16pt; line-height: 1.1; }
          .signin-date { font-size: 12pt; }
          .signin-header { margin-bottom: 0.4rem; }
          .signin-table {
            width: 6.5in;
            margin: 0 auto;
            font-size: 10pt;
            line-height: 1.1;
            table-layout: fixed;
          }
          .signin-table tr { page-break-inside: avoid; }
          .signin-table th, .signin-table td {
            padding: 4px 8px;
            height: 0.25in;
            border: 1px solid #000 !important;
          }
          .signin-table thead th {
            background: #eee !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .signin-table .col-team { width: 0.7in; }
          .signin-table .col-cart { width: 0.7in; }
          .signin-table .col-name { width: 2.1in; }
          .signin-table .col-sig { width: 3.0in; }
          .print-actions { display: none !important; }
        }
      `}</style>

      <div className="print-actions">
        <Button variant="primary" onClick={() => window.print()}>Print All Sheets</Button>
      </div>

      {weeks.map((w) => (
        <div className="signin-sheet" key={w.week_number ?? "single"}>
          <div className="signin-header">
            <h2 className="signin-title">{heading}</h2>
            <div className="signin-date">
              {w.date
                ? formatDate(w.date)
                : <><strong>Date:</strong> ____________________</>}
              {w.week_number ? <span>  —  Week {w.week_number}</span> : null}
            </div>
          </div>

          <table className="signin-table">
            <thead>
              <tr>
                <th className="col-team">Team #</th>
                <th className="col-name">Player Name</th>
                <th className="col-sig">Signature</th>
                <th className="col-cart">Cart #</th>
              </tr>
            </thead>
            <tbody>
              {data.teams.map((team) => (
                <React.Fragment key={team.team_number}>
                  <tr>
                    <td className="col-team" rowSpan={2}><strong>{team.team_number}</strong></td>
                    <td className="col-name">{team.players[0] || " "}</td>
                    <td className="col-sig">&nbsp;</td>
                    <td className="col-cart" rowSpan={2}>&nbsp;</td>
                  </tr>
                  <tr>
                    <td className="col-name">{team.players[1] || " "}</td>
                    <td className="col-sig">&nbsp;</td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ))}

    </div>
  );
}
